import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Facebook, X, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Overline } from "./Motion";
import { BIZ } from "../data/site";

export const PHOTOS = [
  { f: "crew-on-roof", t: "Our crew on the roof", c: ["Roofing", "Crew"] },
  { f: "finished-home", t: "Finished project", c: ["Roofing", "Siding"] },
  { f: "crew-lineup", t: "The Good Home crew", c: ["Crew"] },
  { f: "happy-customers", t: "Happy homeowners", c: ["Crew"] },
  { f: "timberline-shingles", t: "GAF Timberline HDZ on site", c: ["Roofing"] },
  { f: "roof-in-progress", t: "Roof replacement in progress", c: ["Roofing"] },
  { f: "work-van", t: "On the way to your home", c: ["Crew"] },
  { f: "roof-inspection", t: "Walk-around inspection", c: ["Roofing"] },
  { f: "material-delivery", t: "Material delivery day", c: ["Roofing"] },
  { f: "job-site", t: "Before the tear-off", c: ["Roofing"] },
  { f: "consultation", t: "Free estimate visit", c: ["Crew"] },
  { f: "materials", t: "Quality materials", c: ["Roofing"] },
].map((p) => ({ ...p, src: `/img/fb/${p.f}.jpg` }));

export const Lightbox = ({ i, setI, list = PHOTOS }) => {
  const p = list[i];
  const go = (d) => setI((i + d + list.length) % list.length);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/95 p-4" onClick={() => setI(null)} data-testid="photo-lightbox">
      <button className="absolute right-4 top-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Close" data-testid="lightbox-close"><X className="h-5 w-5" /></button>
      <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-3 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
      <figure onClick={(e) => e.stopPropagation()} className="max-w-3xl">
        <img src={p.src} alt={p.t} className="max-h-[80vh] w-auto rounded-xl" />
        <figcaption className="mt-3 text-center text-sm text-slate-300">{p.t}</figcaption>
      </figure>
      <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-3 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Next" data-testid="lightbox-next"><ChevronRight className="h-6 w-6" /></button>
    </motion.div>
  );
};

export const RealPhotos = ({ limit = 9 }) => {
  const [open, setOpen] = useState(null);
  return (
    <section className="bg-white py-20 md:py-28" data-testid="real-photos-section">
      <div className="wrap">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div><Overline>From our Facebook</Overline><h2 className="font-display text-4xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">Real jobs. Real crew.</h2></div>
          <div className="flex flex-wrap gap-2">
            <Link to="/gallery" className="btn-amber" data-testid="real-photos-gallery-link">See full gallery <ArrowRight className="h-4 w-4" /></Link>
            <a href={BIZ.facebook} target="_blank" rel="noreferrer" className="btn-ghost-dark" data-testid="real-photos-facebook-link"><Facebook className="h-4 w-4" />Facebook</a>
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {PHOTOS.slice(0, limit).map((p, i) => (
            <Reveal key={p.f} delay={(i % 4) * 0.06} className={i === 0 ? "col-span-2 row-span-2" : ""}>
              <button onClick={() => setOpen(i)} className="group relative block aspect-square w-full overflow-hidden rounded-2xl bg-slate-100" data-testid={`real-photo-${i}`}>
                <img src={p.src} alt={p.t} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <span className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <span className="absolute bottom-3 left-3 right-3 text-left text-sm font-semibold text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">{p.t}</span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      <AnimatePresence>{open !== null && <Lightbox i={open} setI={setOpen} />}</AnimatePresence>
    </section>
  );
};
